import { makeAutoObservable, reaction, runInAction } from "mobx";
import { userClient, paymentsClient, tradingClient } from "@/shared/rpc/clients";
import { userStore } from "@/shared/stores/UserStore";
import { supportedLocales } from "@/shared/lib/i18n/config";
import { localeStorage } from "@/shared/lib/locale-storage";
import { addToast } from "@cronika/ui";
import { KycStatus } from "@cronika/proto/common/user_pb";
import { TradingProfileAccountScope, TradingProfilePeriodPreset } from "@cronika/proto/client/trading_pb";
import { Code, ConnectError } from "@connectrpc/connect";

/**
 * The eight states from Антифрод-6. Escalation states always take
 * priority over Basic ones: an escalation only exists once Basic has
 * already been approved (support requests it on top of an approved
 * account), so if one of the three escalation flags is set, Basic's own
 * status is stale/irrelevant for what the user should see.
 */
export type VerificationCardState =
  | "not-verified"
  | "pending"
  | "verified"
  | "declined"
  | "escalation-required"
  | "escalation-pending"
  | "escalation-rejected";

class ProfileSettingsStore {
  firstName = "";
  lastName = "";
  dob = "";
  phone = "";
  country = "";
  nickname = "";

  origFirstName = "";
  origLastName = "";
  origDob = "";
  origPhone = "";
  origCountry = "";
  origNickname = "";

  dobError: string | null = null;
  timezone = "";
  isHalalAccountEnabled = false;
  subscribeForNews = true;
  subscribeForStats = true;

  savingField: string | null = null;
  avatarUploading = false;

  nicknameAvailable: boolean | null = null;
  checkingNickname = false;

  kycStatus = KycStatus.UNSPECIFIED;

  kycEscalationActionRequired = false;
  kycEscalationUnderReview = false;
  kycEscalationFinallyDeclined = false;
  kycEscalationFormName = "";
  /**
   * Plain-language, locale-resolved description of the requested check
   * (e.g. "Confirm the document belongs to you"), only set while
   * kycEscalationActionRequired is true. Never the raw admin-facing form
   * name — see GetKycEscalationStatusResponse.check_description.
   */
  kycEscalationCheckDescription = "";
  /** True once the user's most recent (now-resolved) escalation was approved by support — see GetKycEscalationStatusResponse.escalation_approved. */
  kycEscalationApproved = false;
  showKycEscalationModal = false;

  hasDeposited = false;
  hasRealTrades = false;

  cropSrc: string | null = null;
  cropMimeType = "image/jpeg";

  showEmailChangeModal = false;
  showEmailVerificationModal = false;
  showKycModal = false;

  private nicknameDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private prevUserId: string | undefined = undefined;

  constructor() {
    makeAutoObservable(this, {
      nicknameDebounceTimer: false,
      prevUserId: false,
    } as any);

    reaction(
      () => userStore.user,
      (user) => {
        if (user) {
          this.syncFromUser(user);
          void this.loadKycEscalationStatus();
        }
      },
      { fireImmediately: true },
    );
  }

  private syncFromUser(user: NonNullable<typeof userStore.user>) {
    const fn = user.firstName || "";
    const ln = user.lastName || "";
    const rawDob = user.dateOfBirth || "";
    const d = rawDob.includes("-") && rawDob.length === 10 ? rawDob.split("-").reverse().join("/") : rawDob;
    const p = (user as any).phone || "";
    const c = user.countryCode || "";
    const n = (user as any).nickname || "";

    const isNewUser = this.prevUserId !== user.id;
    this.prevUserId = user.id;
    const countryChangedLocally = this.country !== this.origCountry;

    if (isNewUser) {
      this.firstName = fn;
      this.lastName = ln;
      this.dob = d;
      this.phone = p;
      this.country = c;
      this.nickname = n;
    } else if (!countryChangedLocally) {
      this.country = c;
    }

    this.origFirstName = fn;
    this.origLastName = ln;
    this.origDob = d;
    this.origPhone = p;
    this.origCountry = c;
    this.origNickname = n;
    this.timezone = user.timezone || "";
    this.isHalalAccountEnabled = user.halalEnabled || false;
    this.subscribeForNews = user.subscribeForNews ?? true;
    this.subscribeForStats = user.subscribeForStats ?? true;
  }

  // --- Computed ---

  get maxDob(): Date {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 18);
    return d;
  }

  get firstNameLocked() {
    return !!userStore.user?.nameSetByUser;
  }

  get lastNameLocked() {
    return !!userStore.user?.nameSetByUser;
  }

  get dobLocked() {
    return !!this.origDob;
  }

  get countryLocked() {
    return this.isKycApproved;
  }

  get isEmailVerified() {
    return !!userStore.user?.emailVerifiedAt;
  }

  get isKycApproved() {
    return this.kycStatus === KycStatus.APPROVED;
  }

  get isKycPending() {
    return this.kycStatus === KycStatus.PENDING;
  }

  get isKycDeclined() {
    return this.kycStatus === KycStatus.DECLINED;
  }

  get isKycRequired() {
    return !!userStore.user?.kycRequired;
  }

  get isKycEscalationBlocking() {
    return this.kycEscalationActionRequired || this.kycEscalationUnderReview || this.kycEscalationFinallyDeclined;
  }

  get verificationCardState(): VerificationCardState {
    if (this.kycEscalationFinallyDeclined) return "escalation-rejected";
    if (this.kycEscalationUnderReview) return "escalation-pending";
    if (this.kycEscalationActionRequired) return "escalation-required";
    if (this.isKycApproved) return "verified";
    if (this.isKycPending) return "pending";
    if (this.isKycDeclined) return "declined";
    return "not-verified";
  }

  get kycRequiredReason() {
    return userStore.user?.kycRequiredReason ?? "";
  }

  get isPayoutRestricted() {
    return !!userStore.user?.payoutRestrictionActive;
  }

  get payoutRestrictedUntil() {
    return userStore.user?.payoutRestrictedUntil ?? "";
  }

  get verifiedCount() {
    return (this.isEmailVerified ? 1 : 0) + (this.hasDeposited ? 1 : 0) + (this.hasRealTrades ? 1 : 0);
  }

  get totalCount() {
    return 3;
  }

  get verificationPercent() {
    return Math.round((this.verifiedCount / this.totalCount) * 100);
  }

  get isVerificationComplete() {
    return this.verificationPercent === 100;
  }

  get shortId() {
    return userStore.user?.id ? userStore.user.id.replace(/-/g, "").slice(-6) : "";
  }

  // --- Simple setters (needed to stay inside action context) ---

  setFirstName(v: string) {
    this.firstName = v;
  }

  setLastName(v: string) {
    this.lastName = v;
  }

  setPhone(v: string) {
    this.phone = v.replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "");
  }

  setCountry(v: string) {
    if (this.countryLocked) return;
    this.country = v;
  }

  openEmailChangeModal() {
    this.showEmailChangeModal = true;
  }

  closeEmailChangeModal() {
    this.showEmailChangeModal = false;
  }

  openEmailVerificationModal() {
    this.showEmailVerificationModal = true;
  }

  closeEmailVerificationModal() {
    this.showEmailVerificationModal = false;
  }

  openKycModal() {
    this.showKycModal = true;
  }

  closeKycModal() {
    this.showKycModal = false;
  }

  setKycPending() {
    this.kycStatus = KycStatus.PENDING;
  }

  /**
   * Optimistic client-side flip, mirroring setKycPending() above. KYCAID's
   * own docs say the completion webhook can take "2 minutes to 6 hours" to
   * arrive — without this, a user who just finished the iframe would still
   * see "action required" (backend status stuck on REQUESTED) for up to 6
   * hours. A later loadKycEscalationStatus() (e.g. on next page load)
   * reconciles with the real backend state once the webhook lands.
   */
  setKycEscalationSubmitted() {
    this.kycEscalationActionRequired = false;
    this.kycEscalationUnderReview = true;
  }

  // --- DOB ---

  handleDobInput(rawValue: string, invalidDateMsg: string) {
    const digits = rawValue.replace(/\D/g, "").slice(0, 8);
    let masked = digits;
    if (digits.length > 4) masked = digits.slice(0, 2) + "/" + digits.slice(2, 4) + "/" + digits.slice(4);
    else if (digits.length > 2) masked = digits.slice(0, 2) + "/" + digits.slice(2);

    if (digits.length === 8) {
      if (!this.isValidDobString(masked)) {
        this.dobError = invalidDateMsg;
      } else {
        const entered = new Date(`${digits.slice(4)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}`);
        if (entered > this.maxDob) {
          masked = `${String(this.maxDob.getDate()).padStart(2, "0")}/${String(this.maxDob.getMonth() + 1).padStart(2, "0")}/${this.maxDob.getFullYear()}`;
        }
        this.dobError = null;
      }
    } else {
      this.dobError = null;
    }
    this.dob = masked;
  }

  selectDobFromCalendar(date: Date) {
    this.dob = [
      String(date.getDate()).padStart(2, "0"),
      String(date.getMonth() + 1).padStart(2, "0"),
      date.getFullYear(),
    ].join("/");
  }

  isValidDobString(s: string): boolean {
    const parts = s.split("/");
    if (parts.length !== 3 || (parts[2] ?? "").length !== 4) return false;
    const day = parseInt(parts[0] ?? "", 10);
    const month = parseInt(parts[1] ?? "", 10);
    const year = parseInt(parts[2] ?? "", 10);
    if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) return false;
    const d = new Date(`${parts[2]}-${(parts[1] ?? "").padStart(2, "0")}-${(parts[0] ?? "").padStart(2, "0")}`);
    if (isNaN(d.getTime())) return false;
    return d.getFullYear() === year && d.getMonth() + 1 === month && d.getDate() === day;
  }

  parseDobToISO(s: string): string | undefined {
    const parts = s.split("/");
    if (parts.length !== 3 || (parts[2] ?? "").length !== 4) return undefined;
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }

  parseDobToDate(s: string): Date | undefined {
    const parts = s.split("/");
    if (parts.length !== 3 || (parts[2] ?? "").length !== 4) return undefined;
    const d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    return isNaN(d.getTime()) ? undefined : d;
  }

  // --- Nickname ---

  setNickname(value: string) {
    this.nickname = value;
    this.checkNicknameAvailability(value);
  }

  private checkNicknameAvailability(value: string) {
    if (this.nicknameDebounceTimer) clearTimeout(this.nicknameDebounceTimer);
    if (!value || value === this.origNickname) {
      this.nicknameAvailable = null;
      this.checkingNickname = false;
      return;
    }
    this.checkingNickname = true;
    this.nicknameDebounceTimer = setTimeout(async () => {
      try {
        const res = await userClient.checkNicknameAvailable({ nickname: value });
        runInAction(() => {
          this.nicknameAvailable = res.available;
        });
      } catch {
        runInAction(() => {
          this.nicknameAvailable = null;
        });
      } finally {
        runInAction(() => {
          this.checkingNickname = false;
        });
      }
    }, 500);
  }

  // --- Field save / cancel ---

  async saveField(
    field: "firstName" | "lastName" | "dob" | "phone" | "country" | "nickname",
    msgs: { taken: string; saved: string; countryLocked?: string },
  ) {
    if (field === "nickname" && this.nicknameAvailable === false) {
      addToast({ title: msgs.taken, color: "danger", timeout: 5000 });
      return;
    }
    const countryBeforeSave = this.origCountry;
    this.savingField = field;
    try {
      await userClient.updateProfile({
        firstName: field === "firstName" ? this.firstName || undefined : undefined,
        lastName: field === "lastName" ? this.lastName || undefined : undefined,
        dateOfBirth: field === "dob" ? (this.dob ? this.parseDobToISO(this.dob) : undefined) : undefined,
        countryCode: field === "country" ? this.country || undefined : undefined,
        nickname: field === "nickname" ? this.nickname || undefined : undefined,
        phone: field === "phone" ? this.phone || undefined : undefined,
      });
      await userStore.getProfile();
      runInAction(() => {
        if (field === "firstName") this.origFirstName = this.firstName;
        if (field === "lastName") this.origLastName = this.lastName;
        if (field === "dob") this.origDob = this.dob;
        if (field === "phone") this.origPhone = this.phone;
        if (field === "country") this.origCountry = this.country;
        if (field === "nickname") this.origNickname = this.nickname;
      });
      addToast({ title: msgs.saved, color: "success", timeout: 5000 });
    } catch (error) {
      const isCountryLockedError =
        field === "country" &&
        ((error instanceof ConnectError && error.code === Code.FailedPrecondition) ||
          String((error as { message?: unknown })?.message ?? error).includes("Country cannot be changed after verification"));

      if (isCountryLockedError) {
        runInAction(() => {
          this.country = countryBeforeSave;
          this.origCountry = countryBeforeSave;
        });
        addToast({
          title: msgs.countryLocked!,
          color: "danger",
          timeout: 5000,
        });
        await Promise.all([userStore.getProfile(), this.loadKycStatus()]);
        return;
      }
      console.error("Failed to update profile:", error);
    } finally {
      runInAction(() => {
        this.savingField = null;
      });
    }
  }

  cancelField(field: "firstName" | "lastName" | "dob" | "phone" | "country" | "nickname") {
    if (field === "firstName") this.firstName = this.origFirstName;
    if (field === "lastName") this.lastName = this.origLastName;
    if (field === "dob") {
      this.dob = this.origDob;
      this.dobError = null;
    }
    if (field === "phone") this.phone = this.origPhone;
    if (field === "country") this.country = this.origCountry;
    if (field === "nickname") {
      this.nickname = this.origNickname;
      this.nicknameAvailable = null;
    }
  }

  // --- Settings ---

  async updateTimezone(newTimezone: string, successMsg?: string) {
    this.timezone = newTimezone;
    try {
      await userClient.updateProfile({ timezone: newTimezone });
      await userStore.getProfile();
      if (successMsg) {
        addToast({ title: successMsg, color: "success", timeout: 5000 });
      }
    } catch (error) {
      console.error(error);
    }
  }

  async updateHalal(value: boolean, successMsg: string) {
    this.isHalalAccountEnabled = value;
    try {
      await userClient.updateProfile({ halalEnabled: value });
      await userStore.getProfile();
      addToast({ title: successMsg, color: "success", timeout: 5000 });
    } catch {
      runInAction(() => {
        this.isHalalAccountEnabled = userStore.user?.halalEnabled || false;
      });
    }
  }

  async updateSubscribeForNews(value: boolean, successMsg: string) {
    this.subscribeForNews = value;
    try {
      await userClient.updateProfile({ subscribeForNews: value });
      await userStore.getProfile();
      addToast({ title: successMsg, color: "success", timeout: 5000 });
    } catch {
      runInAction(() => {
        this.subscribeForNews = userStore.user?.subscribeForNews ?? true;
      });
    }
  }

  async updateSubscribeForStats(value: boolean, successMsg: string) {
    this.subscribeForStats = value;
    try {
      await userClient.updateProfile({ subscribeForStats: value });
      await userStore.getProfile();
      addToast({ title: successMsg, color: "success", timeout: 5000 });
    } catch {
      runInAction(() => {
        this.subscribeForStats = userStore.user?.subscribeForStats ?? true;
      });
    }
  }

  async changeLang(newLang: string, currentPathname: string, navigate: (path: string) => void) {
    const segments = currentPathname.split("/").filter(Boolean);
    const pathWithoutLocale = supportedLocales.includes(segments[0] as any)
      ? `/${segments.slice(1).join("/")}`
      : currentPathname;
    const normalizedPath = pathWithoutLocale === "/" ? "" : pathWithoutLocale;
    navigate(`/${newLang}${normalizedPath}`);
    localeStorage.set(newLang);
    try {
      await userClient.updateProfile({ language: newLang });
      await userStore.getProfile();
    } catch (error) {
      console.error(error);
    }
  }

  // --- KYC ---

  async loadKycStatus() {
    try {
      const res = await userClient.getKycStatus({});
      runInAction(() => {
        this.kycStatus = res.status;
      });
    } catch {}
  }

  async loadKycEscalationStatus() {
    try {
      const res = await userClient.getKycEscalationStatus({});
      runInAction(() => {
        this.kycEscalationActionRequired = res.actionRequired;
        this.kycEscalationUnderReview = res.underReview;
        this.kycEscalationFinallyDeclined = res.finallyDeclined;
        this.kycEscalationFormName = res.formName ?? "";
        this.kycEscalationCheckDescription = res.checkDescription ?? "";
        this.kycEscalationApproved = res.escalationApproved;
      });
    } catch {}
  }

  openKycEscalationModal() {
    this.showKycEscalationModal = true;
  }

  closeKycEscalationModal() {
    this.showKycEscalationModal = false;
  }

  // --- Onboarding checklist ---

  async loadOnboardingStatus() {
    try {
      const [depositRes, tradingRes] = await Promise.all([
        paymentsClient.getDepositAvailability({}),
        tradingClient.getTradingProfileAnalytics({
          accountScope: TradingProfileAccountScope.REAL,
          periodPreset: TradingProfilePeriodPreset.ALL_TIME,
          timeZone: userStore.user?.timezone || "UTC",
        }),
      ]);
      runInAction(() => {
        this.hasDeposited = depositRes.hasDeposited;
        this.hasRealTrades = tradingRes.availability?.hasRealTrades ?? false;
      });
    } catch {}
  }

  // --- Avatar ---

  selectAvatarFile(file: File, msgs: { invalidType: string; tooLarge: string }): boolean {
    const ALLOWED = ["image/jpeg", "image/png", "image/webp"];
    if (!ALLOWED.includes(file.type)) {
      addToast({ title: msgs.invalidType, color: "danger", timeout: 5000 });
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      addToast({ title: msgs.tooLarge, color: "danger", timeout: 5000 });
      return false;
    }
    const reader = new FileReader();
    reader.onload = () => {
      runInAction(() => {
        this.cropMimeType = file.type;
        this.cropSrc = reader.result as string;
      });
    };
    reader.readAsDataURL(file);
    return true;
  }

  async deleteAvatar(msgs: { deleted: string; error: string }) {
    this.avatarUploading = true;
    try {
      await userClient.deleteAvatar({});
      await userStore.getProfile();
      addToast({ title: msgs.deleted, color: "success", timeout: 5000 });
    } catch {
      addToast({ title: msgs.error, color: "danger", timeout: 5000 });
    } finally {
      runInAction(() => {
        this.avatarUploading = false;
      });
    }
  }

  async saveCroppedAvatar(blob: Blob, mimeType: string, msgs: { updated: string; error: string }) {
    this.cropSrc = null;
    this.avatarUploading = true;
    try {
      const arrayBuffer = await blob.arrayBuffer();
      await userClient.uploadAvatar({ data: new Uint8Array(arrayBuffer), mimeType });
      await userStore.getProfile();
      addToast({ title: msgs.updated, color: "success", timeout: 5000 });
    } catch {
      addToast({ title: msgs.error, color: "danger", timeout: 5000 });
    } finally {
      runInAction(() => {
        this.avatarUploading = false;
      });
    }
  }

  cancelCrop() {
    this.cropSrc = null;
  }
}

export const profileSettingsStore = new ProfileSettingsStore();
